import { useCallback, useEffect, useRef, useState } from "react";
import {
	type CanvasCard,
	MAX_ZOOM,
	MIN_ZOOM,
	ZOOM_SENSITIVITY,
} from "./canvas-utils";

interface UseCanvasViewportOptions {
	cards: CanvasCard[];
	isChatOpen: boolean;
}

/** Canvas pan offset + zoom: wheel/pinch handling, zoom buttons, fit-all. */
export function useCanvasViewport({ cards, isChatOpen }: UseCanvasViewportOptions) {
	const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 });
	const [zoom, setZoom] = useState(1);

	const canvasRef = useRef<HTMLDivElement>(null);

	const zoomRef = useRef(zoom);
	zoomRef.current = zoom;

	const canvasOffsetRef = useRef(canvasOffset);
	canvasOffsetRef.current = canvasOffset;

	useEffect(() => {
		const el = canvasRef.current;
		if (!el) return;

		const onWheel = (e: WheelEvent) => {
			e.preventDefault();
			e.stopPropagation();

			// Ctrl+scroll (or pinch) = zoom
			if (e.ctrlKey || e.metaKey) {
				const rect = el.getBoundingClientRect();
				const cursorX = e.clientX - rect.left;
				const cursorY = e.clientY - rect.top;

				const currentZoom = zoomRef.current;
				const delta = -e.deltaY * ZOOM_SENSITIVITY;
				const newZoom = Math.min(
					MAX_ZOOM,
					Math.max(MIN_ZOOM, currentZoom * (1 + delta)),
				);
				const zoomRatio = newZoom / currentZoom;

				setCanvasOffset((prev) => ({
					x: cursorX - (cursorX - prev.x) * zoomRatio,
					y: cursorY - (cursorY - prev.y) * zoomRatio,
				}));
				setZoom(newZoom);
			} else {
				// Normal scroll = pan
				setCanvasOffset((prev) => ({
					x: prev.x - e.deltaX,
					y: prev.y - e.deltaY,
				}));
			}
		};

		el.addEventListener("wheel", onWheel, { passive: false });
		return () => el.removeEventListener("wheel", onWheel);
	}, [isChatOpen]);

	const handleZoomIn = useCallback(() => {
		const container = canvasRef.current;
		if (!container) return;
		const rect = container.getBoundingClientRect();
		const cx = rect.width / 2;
		const cy = rect.height / 2;
		const newZoom = Math.min(MAX_ZOOM, zoom * 1.2);
		const ratio = newZoom / zoom;
		setCanvasOffset((prev) => ({
			x: cx - (cx - prev.x) * ratio,
			y: cy - (cy - prev.y) * ratio,
		}));
		setZoom(newZoom);
	}, [zoom]);

	const handleZoomOut = useCallback(() => {
		const container = canvasRef.current;
		if (!container) return;
		const rect = container.getBoundingClientRect();
		const cx = rect.width / 2;
		const cy = rect.height / 2;
		const newZoom = Math.max(MIN_ZOOM, zoom / 1.2);
		const ratio = newZoom / zoom;
		setCanvasOffset((prev) => ({
			x: cx - (cx - prev.x) * ratio,
			y: cy - (cy - prev.y) * ratio,
		}));
		setZoom(newZoom);
	}, [zoom]);

	const handleResetView = useCallback(() => {
		setZoom(1);
		setCanvasOffset({ x: 0, y: 0 });
	}, []);

	const handleFitAll = useCallback(() => {
		if (cards.length === 0 || !canvasRef.current) return;

		const rect = canvasRef.current.getBoundingClientRect();
		const viewW = rect.width;
		const viewH = rect.height;
		const PAD = 80;

		// Compute bounding box of all cards
		let minX = Infinity,
			minY = Infinity,
			maxX = -Infinity,
			maxY = -Infinity;
		for (const c of cards) {
			minX = Math.min(minX, c.x);
			minY = Math.min(minY, c.y);
			maxX = Math.max(maxX, c.x + c.width);
			maxY = Math.max(maxY, c.y + c.height);
		}

		const contentW = maxX - minX;
		const contentH = maxY - minY;
		if (contentW <= 0 || contentH <= 0) return;

		const newZoom = Math.min(
			(viewW - PAD * 0.1) / contentW,
			(viewH - PAD * 0.1) / contentH,
			MAX_ZOOM,
		);
		const clampedZoom = Math.max(MIN_ZOOM, newZoom);

		const offsetX = (viewW - contentW * clampedZoom) / 2 - minX * clampedZoom;
		const offsetY = (viewH - contentH * clampedZoom) / 2 - minY * clampedZoom;

		setZoom(clampedZoom);
		setCanvasOffset({ x: offsetX, y: offsetY });
	}, [cards]);

	// Percent label
	const zoomPercent = Math.round(zoom * 100);

	return {
		canvasRef,
		canvasOffset,
		setCanvasOffset,
		zoom,
		setZoom,
		zoomPercent,
		handleZoomIn,
		handleZoomOut,
		handleResetView,
		handleFitAll,
	};
}
